import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getStory, getManifest, approveManifest, listCharacters, listLocations, listProps } from "@/lib/api";
import RingBadge from "@/components/RingBadge";
import StatusBadge from "@/components/StatusBadge";
import ShotRow from "@/components/ShotRow";
import ShotEditDialog from "@/components/ShotEditDialog";
import ShotAttemptDialog from "@/components/ShotAttemptDialog";
import CharacterMappingBar from "@/components/CharacterMappingBar";
import LocationPropMappingBar from "@/components/LocationPropMappingBar";
import ContinuityChainPanel from "@/components/ContinuityChainPanel";
import UnlockManifestDialog from "@/components/UnlockManifestDialog";
import BatchRunner from "@/components/BatchRunner";
import ExportPanel from "@/components/ExportPanel";
import LockHistory from "@/components/LockHistory";
import { Button } from "@/components/ui/button";
import { LockSimple, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ManifestReview() {
  const { storyId } = useParams();
  const [story, setStory] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [shots, setShots] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [locations, setLocations] = useState([]);
  const [props, setProps] = useState([]);
  const [editingShot, setEditingShot] = useState(null);
  const [viewingAttemptShot, setViewingAttemptShot] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [approving, setApproving] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const pollRef = useRef(null);

  const load = async () => {
    const [s, m, c, l, p] = await Promise.all([
      getStory(storyId), getManifest(storyId), listCharacters(storyId), listLocations(storyId), listProps(storyId),
    ]);
    setStory(s);
    setScenes(m.scenes.sort((a, b) => a.scene_number - b.scene_number));
    setShots(m.shots);
    setCharacters(c);
    setLocations(l);
    setProps(p);
    return m.shots;
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyId]);

  const startPolling = () => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      const latestShots = await load();
      const stillRunning = latestShots.some((s) => s.gen_status === "queued" || s.gen_status === "generating");
      if (!stillRunning) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        toast.success("Batch generation complete");
      }
    }, 4000);
  };

  useEffect(() => () => pollRef.current && clearInterval(pollRef.current), []);

  const handleApprove = async () => {
    setApproving(true);
    try {
      const res = await approveManifest(storyId);
      toast.success("Manifest LOCKED — no creative decisions downstream");
      toast.message("Telegram ping (stub)", { description: res.notification.message });
      setStory((s) => ({ ...s, status: "locked" }));
      setRefreshKey((k) => k + 1);
      await load(); // re-fetch shots — the backend just computed the dependency graph, need it in view
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to approve manifest");
    } finally {
      setApproving(false);
    }
  };

  const handleUnlocked = () => {
    setStory((s) => ({ ...s, status: "manifest_review" }));
    setRefreshKey((k) => k + 1);
  };

  const toggleSelect = (shotId) => {
    setSelectedIds((prev) => (prev.includes(shotId) ? prev.filter((id) => id !== shotId) : [...prev, shotId]));
  };

  const handleSelectAll = () => {
    setSelectedIds((prev) => (prev.length === shots.length ? [] : shots.map((s) => s.id)));
  };

  if (!story) return <div className="p-6 text-slate-500 text-sm">Loading…</div>;

  const isLocked = story.status === "locked";
  const shotsByScene = (sceneNumber) => shots.filter((sh) => sh.scene_number === sceneNumber).sort((a, b) => a.order_index - b.order_index);

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="manifest-review-page">
      <div className="sticky top-0 z-10 bg-slate-950 pb-4 border-b border-slate-800 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="font-heading uppercase tracking-wider text-2xl font-bold text-slate-50">{story.title}</h1>
            <RingBadge ring={story.ring_tag} />
            <StatusBadge status={story.status} />
            <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500 border border-slate-800 px-2 py-0.5 rounded-sm">
              {story.viewer_mode}
            </span>
          </div>
          {isLocked ? (
            <div className="flex items-center gap-3">
              <span data-testid="manifest-locked-banner" className="flex items-center gap-2 text-emerald-400 font-mono text-xs uppercase tracking-widest">
                <LockSimple size={14} /> Locked — no creative decisions downstream
              </span>
              <UnlockManifestDialog storyId={storyId} onUnlocked={handleUnlocked} />
            </div>
          ) : (
            <Button
              data-testid="approve-lock-manifest-btn"
              onClick={handleApprove}
              disabled={approving}
              className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm"
            >
              <CheckCircle size={16} className="mr-2" />
              {approving ? "Locking…" : "Approve & Lock Manifest"}
            </Button>
          )}
        </div>
        <div className="flex items-center justify-between">
          <p className="font-mono text-xs text-slate-500">
            {scenes.length} scenes · {shots.length} shots
          </p>
          <LockHistory storyId={storyId} refreshKey={refreshKey} />
        </div>
      </div>

      <CharacterMappingBar
        storyId={storyId}
        shots={shots}
        characters={characters}
        viewerMode={story.viewer_mode}
        isLocked={isLocked}
        onRemapped={load}
      />

      <LocationPropMappingBar
        storyId={storyId}
        shots={shots}
        locations={locations}
        props={props}
        isLocked={isLocked}
        onChanged={load}
      />

      <ContinuityChainPanel shots={shots} />

      {isLocked && (
        <BatchRunner
          storyId={storyId}
          story={story}
          shots={shots}
          selectedIds={selectedIds}
          onSelectAll={handleSelectAll}
          onBatchStarted={startPolling}
          onBudgetUpdated={(updated) => setStory(updated)}
        />
      )}

      {isLocked && (
        <ExportPanel storyId={storyId} refreshSignal={`${refreshKey}-${shots.filter((s) => s.gen_status === "passed").length}`} />
      )}

      <div className="space-y-8">
        {scenes.map((scene) => (
          <div key={scene.id} data-testid={`scene-block-${scene.id}`}>
            <div className="flex items-baseline gap-3 mb-2">
              <span className="font-mono text-xs uppercase tracking-widest text-slate-600">ACT {scene.act}</span>
              <h2 className="font-heading uppercase tracking-wider text-lg font-semibold text-slate-100">
                Scene {scene.scene_number} — {scene.title}
              </h2>
            </div>
            {scene.summary && <p className="text-xs text-slate-500 mb-2 ml-1">{scene.summary}</p>}
            <div className="border border-slate-800 rounded-sm">
              {shotsByScene(scene.scene_number).map((shot) => (
                <ShotRow
                  key={shot.id}
                  shot={shot}
                  isLocked={isLocked}
                  onEdit={setEditingShot}
                  onDeleted={(id) => setShots((prev) => prev.filter((s) => s.id !== id))}
                  onMoved={load}
                  selectable={isLocked}
                  selected={selectedIds.includes(shot.id)}
                  onToggleSelect={toggleSelect}
                  onOpenAttempt={setViewingAttemptShot}
                />
              ))}
              {shotsByScene(scene.scene_number).length === 0 && (
                <p className="text-xs text-slate-600 p-4">No shots in this scene.</p>
              )}
            </div>
          </div>
        ))}
      </div>

      <ShotEditDialog
        shot={editingShot}
        open={!!editingShot}
        onOpenChange={(v) => !v && setEditingShot(null)}
        onSaved={(updated) => setShots((prev) => prev.map((s) => (s.id === updated.id ? updated : s)))}
      />
      <ShotAttemptDialog
        shot={viewingAttemptShot}
        open={!!viewingAttemptShot}
        onOpenChange={(v) => !v && setViewingAttemptShot(null)}
        entityOptions={{ character: characters, location: locations, prop: props }}
        onShotUpdated={(updated) => {
          setShots((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
          setViewingAttemptShot(updated);
          startPolling(); // approve/reject/set-priority can trigger a new background wave
        }}
      />
    </div>
  );
}
